/**
 * Mobile Phase 4 (t182u): the phone Rota tab. Keys match the desktop schedule's (qk.wf(ws, 'schedule', building,
 * from, to), 'attendance', 'employees') so both layouts share one cache, and every change invalidates the 'wf' prefix
 * (rota, My shifts, Today, approvals). Rota changes are online-only: none of these go through the outbox
 * (docs/offline.md §3.5).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BulkShiftOp, ShiftInput } from '@tradeoutlet/shared';
import { dataApi } from './client';
import { qk } from './keys';

type Ws = string | null | undefined;
export const rotaKeys = {
  week: (ws: Ws, location: string, from: string, to: string) => qk.wf(ws ?? '', 'schedule', location, from, to),
  attendance: (ws: Ws, date: string) => qk.wf(ws ?? '', 'attendance', date),
  employees: (ws: Ws, location: string) => qk.wf(ws ?? '', 'employees', location),
};

/** The week's shifts at one building (or several, t200u), drafts included (what a rota editor sees). `to` is inclusive. */
export function useRotaWeek(ws: Ws, location: string | readonly string[] | null | undefined, from: string, to: string) {
  const many = Array.isArray(location) ? [...location].sort() : null;
  const one = typeof location === 'string' ? location : null;
  return useQuery({
    queryKey: rotaKeys.week(ws, many ? `all:${many.join(',')}` : one ?? '', from, to),
    queryFn: () => dataApi().wf.shifts(ws!, { from, to, ...(many ? { building_ids: many } : { location_id: one! }), include_drafts: true }),
    enabled: Boolean(ws && (many ? many.length : one)), placeholderData: prev => prev,
  });
}
export function useAttendanceDay(ws: Ws, date: string, enabled = true) {
  return useQuery({ queryKey: rotaKeys.attendance(ws, date), queryFn: () => dataApi().wf.attendance(ws!, date), enabled: Boolean(ws) && enabled, refetchInterval: 60_000 });
}
export function useRotaEmployees(ws: Ws, location: string | null | undefined) {
  return useQuery({ queryKey: rotaKeys.employees(ws, location ?? ''), queryFn: () => dataApi().wf.employees(ws!, { location_id: location! }), enabled: Boolean(ws && location), staleTime: 60_000 });
}

function useWfMutation<V, R>(ws: Ws, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ['wf', ws] }) });
}
export type SaveShiftInput = { id?: undefined; body: ShiftInput } | { id: string; body: Partial<ShiftInput> & { version: number } };
/** Add (a draft) or edit one shift; the server returns its warnings. */
export function useSaveShift(ws: Ws) {
  return useWfMutation(ws, (v: SaveShiftInput) => (v.id ? dataApi().wf.updateShift(ws!, v.id, v.body) : dataApi().wf.createShift(ws!, v.body as ShiftInput)));
}
export function useDeleteShift(ws: Ws) {
  return useWfMutation(ws, (id: string) => dataApi().wf.deleteShift(ws!, id));
}
/** Copy, repeat, copy a day, move: the desktop's bulk ops (stops at the first failure and says which). */
export function useBulkShifts(ws: Ws) {
  return useWfMutation(ws, (ops: BulkShiftOp[]) => dataApi().wf.bulkShifts(ws!, ops));
}
export function useCopyWeek(ws: Ws) {
  return useWfMutation(ws, (b: { location_id: string; from_week: string; to_week: string; include_assignments?: boolean }) => dataApi().wf.copyWeek(ws!, b));
}
/** Publish with notify, the same call and choices as the desktop dialog. */
export function usePublishRota(ws: Ws) {
  return useWfMutation(ws, (b: { location_id: string; from: string; to: string; notify: 'changed' | 'all' | 'none'; shift_ids?: string[] }) => dataApi().wf.publish(ws!, b));
}

// ------------------------------------------------------------------------------------------------ approvals inbox
/** Same keys as the desktop pages (schedule requests, time off, timesheets) so approving in one place clears the other. */
export const approvalKeys = {
  claims: (ws: Ws) => qk.wf(ws ?? '', 'claims', 'pending'),
  swaps: (ws: Ws) => qk.wf(ws ?? '', 'swaps', 'manage'),
  leave: (ws: Ws) => qk.wf(ws ?? '', 'leave', 'manage'),
  timesheets: (ws: Ws, from: string, to: string) => qk.wf(ws ?? '', 'timesheets', 'manage', from, to, 'review'),
};
export interface ApprovalQueryOptions { claims: boolean; swaps: boolean; leave: boolean; timesheets: boolean; from: string; to: string }
/** The four lists the inbox merges; each only runs when you hold its approve permission. */
export function useApprovalSources(ws: Ws, o: ApprovalQueryOptions) {
  const on = Boolean(ws);
  const claims = useQuery({ queryKey: approvalKeys.claims(ws), queryFn: () => dataApi().wf.claims(ws!, { status: 'pending' }), enabled: on && o.claims });
  const swaps = useQuery({ queryKey: approvalKeys.swaps(ws), queryFn: () => dataApi().wf.swaps(ws!, { scope: 'manage', status: 'pending_manager' }), enabled: on && o.swaps });
  const leave = useQuery({ queryKey: approvalKeys.leave(ws), queryFn: () => dataApi().wf.leaveRequests(ws!, { scope: 'manage' }), enabled: on && o.leave });
  const timesheets = useQuery({ queryKey: approvalKeys.timesheets(ws, o.from, o.to), queryFn: () => dataApi().wf.timesheets(ws!, { from: o.from, to: o.to, scope: 'manage', status: 'review' }), enabled: on && o.timesheets });
  const qs = [claims, swaps, leave, timesheets];
  return {
    claims: claims.data?.claims, swaps: swaps.data?.requests, leave: leave.data?.requests, timesheets: timesheets.data?.entries,
    isLoading: qs.some(q => q.isLoading), error: qs.find(q => q.error)?.error ?? null,
    refetch: () => Promise.all(qs.map(q => q.refetch())),
  };
}
export type ApprovalDecision = { kind: 'claims' | 'swaps' | 'drops' | 'leave' | 'timesheets'; id: string; approve: boolean; note?: string };
/** Approve or decline one item (online only); refreshes the 'wf' lists, Today and the nav badges. */
export function useDecideApproval(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (d: ApprovalDecision): Promise<unknown> => {
      const a = dataApi().wf;
      if (d.kind === 'claims') return a.decideClaim(ws!, d.id, d.approve);
      if (d.kind === 'swaps' || d.kind === 'drops') return a.swapAction(ws!, d.id, d.approve ? 'approve' : 'reject', d.note);
      if (d.kind === 'leave') return a.leaveAction(ws!, d.id, d.approve ? 'approve' : 'reject', d.note);
      return a.timesheetAction(ws!, d.id, d.approve ? 'approve' : 'reject', d.note);
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['wf', ws] }); void qc.invalidateQueries({ queryKey: qk.navBadges(ws ?? '') }); },
  });
}
/** Offer an open shift to people (first to accept gets it). */
export function useOfferShift(ws: Ws) {
  return useWfMutation(ws, (b: { shift_id: string; user_ids: string[]; message?: string | null }) => dataApi().wf.createShiftOffer(ws!, { ...b, mode: 'first_accept' }));
}
